import type { BuildingContext, Message } from '@quipex/shared/contracts';
import { generateMessageId, generateChatId } from '../mapper';
import type {
    QuippyBuildingSession,
    QuippyMessage,
    QuippyScope,
    QuippyThread,
} from '../models';

export const QUIPPY_BUILDING_INTRO_MESSAGE =
    "Hi! I'm Quippy, your AI building assistant. I have access to all your building documents and Quipex platform data. Ask me anything about your building!";
export const QUIPPY_PORTFOLIO_INTRO_MESSAGE =
    "Hi! I'm Quippy, your AI portfolio assistant. I can help you reason across your buildings, documents, maintenance, and compliance signals in Quipex.";
export const QUIPPY_INTRO_MESSAGE = QUIPPY_BUILDING_INTRO_MESSAGE;

export interface RetryContext {
    userInput: string;
    conversationHistory: Message[];
    selectedFileId?: string;
    documentSelectionMessageId?: string;
}

export function createEmptySession(
    options: {
        buildingContext?: BuildingContext;
    } = {}
): QuippyBuildingSession {
    return {
        threads: [],
        draftByChat: {},
        buildingContext: options.buildingContext,
    };
}

export function getSidebarThreads(
    session: QuippyBuildingSession
): QuippyThread[] {
    return session.threads.filter((thread) =>
        thread.messages.some((message) => message.role === 'user')
    );
}

export function findThread(
    session: QuippyBuildingSession,
    chatId: string
): QuippyThread | undefined {
    return session.threads.find((thread) => thread.id === chatId);
}

export function hasPendingAssistantMessage(
    thread: QuippyThread | null | undefined
): boolean {
    return !!thread?.messages.some(
        (message) =>
            message.role === 'assistant' && message.status === 'pending'
    );
}

export function findRetryContext(
    thread: QuippyThread,
    messageId: string
): RetryContext | null {
    const errorIndex = thread.messages.findIndex(
        (message) => message.id === messageId
    );
    if (errorIndex <= 0) return null;

    const errorMessage = thread.messages[errorIndex];
    const previousMessage = thread.messages[errorIndex - 1];
    if (
        errorMessage.retryInput &&
        previousMessage?.content !== errorMessage.retryInput
    ) {
        const selectedFileId = errorMessage.retrySelectedFileId;
        return {
            userInput: errorMessage.retryInput,
            selectedFileId,
            documentSelectionMessageId: selectedFileId
                ? findLatestDocumentSelectionMessageId(
                      thread.messages.slice(0, errorIndex),
                      selectedFileId
                  )
                : undefined,
            conversationHistory: buildConversationHistory(
                thread.messages.slice(0, errorIndex)
            ),
        };
    }

    let userIndex = -1;
    for (let index = errorIndex - 1; index >= 0; index -= 1) {
        if (thread.messages[index].role === 'user') {
            userIndex = index;
            break;
        }
    }

    if (userIndex === -1) return null;

    return {
        userInput: thread.messages[userIndex].content,
        conversationHistory: buildConversationHistory(
            thread.messages.slice(0, userIndex)
        ),
    };
}

function findLatestDocumentSelectionMessageId(
    messages: readonly QuippyMessage[],
    fileId: string
): string | undefined {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        const matchesFileId = message.documentOptions?.some(
            (option) => option.fileId === fileId
        );
        if (matchesFileId) return message.id;
    }

    return undefined;
}

export function buildConversationHistory(
    messages: readonly QuippyMessage[]
): Message[] {
    return messages
        .filter(
            (message) => message.kind !== 'intro' && message.status === 'sent'
        )
        .map((message) => ({
            role: message.role,
            content: message.content,
        }));
}

export function createUserMessage(content: string): QuippyMessage {
    return {
        id: generateMessageId(),
        kind: 'message',
        role: 'user',
        content,
        status: 'sent',
        timestamp: new Date(),
        sources: [],
    };
}

export function createIntroAssistantMessage(
    content = QUIPPY_BUILDING_INTRO_MESSAGE
): QuippyMessage {
    return {
        id: generateMessageId(),
        kind: 'intro',
        role: 'assistant',
        content,
        status: 'sent',
        timestamp: new Date(),
        sources: [],
    };
}

export function createLoadingAssistantMessage(): QuippyMessage {
    return {
        id: generateMessageId(),
        kind: 'message',
        role: 'assistant',
        content: '',
        status: 'pending',
        timestamp: new Date(),
        sources: [],
    };
}

export function createAssistantMessage(
    options: Pick<QuippyMessage, 'content' | 'sources'> &
        Partial<
            Pick<
                QuippyMessage,
                | 'status'
                | 'documentOptions'
                | 'selectedDocumentFileId'
                | 'retryInput'
                | 'retrySelectedFileId'
            >
        >
): QuippyMessage {
    return {
        id: generateMessageId(),
        kind: 'message',
        role: 'assistant',
        content: options.content,
        status: options.status ?? 'sent',
        timestamp: new Date(),
        sources: options.sources,
        documentOptions: options.documentOptions,
        selectedDocumentFileId: options.selectedDocumentFileId,
        retryInput: options.retryInput,
        retrySelectedFileId: options.retrySelectedFileId,
        documentSelectionStatus:
            options.selectedDocumentFileId == null ? undefined : 'selected',
    };
}

export function appendNewThread(
    session: QuippyBuildingSession,
    introMessage = QUIPPY_BUILDING_INTRO_MESSAGE
): QuippyBuildingSession {
    const thread = createThread(introMessage);

    return {
        ...session,
        threads: [thread, ...session.threads],
        draftByChat: {
            ...session.draftByChat,
            [thread.id]: '',
        },
    };
}

export function generateThreadTitle(
    messages: readonly QuippyMessage[]
): string {
    const firstUserMessage = messages.find(
        (message) => message.role === 'user'
    );
    if (!firstUserMessage) return 'New Chat';

    return (
        firstUserMessage.content.slice(0, 40) +
        (firstUserMessage.content.length > 40 ? '...' : '')
    );
}

export function getQuippyIntroMessage(scope: QuippyScope): string {
    return scope.kind === 'portfolio'
        ? QUIPPY_PORTFOLIO_INTRO_MESSAGE
        : QUIPPY_BUILDING_INTRO_MESSAGE;
}

function createThread(introMessage: string): QuippyThread {
    return {
        id: generateChatId(),
        title: 'New Chat',
        lastMessage: '',
        timestamp: new Date(),
        messages: [createIntroAssistantMessage(introMessage)],
    };
}
